import { useEffect, useState } from 'react';
import { useRevalidator } from 'react-router';

import { Badge, Button, Card, Icon } from '@ds/core';
import { Alert, EmptyState, Modal } from '@ds/feedback';
import { Checkbox, FormField, Input, Select, Slider, Switch, Textarea } from '@ds/forms';
import { useAviso } from '@/comuns/toast';
import { useAlteracaoPendente } from '@/comuns/useAlteracaoPendente';
import { useConfirmacao } from '@/comuns/useConfirmacao';
import {
    CATEGORIAS_DE_FERRAMENTA, FERRAMENTAS, TONS, descreverTemperatura, ferramentaPorId, tomPorId,
} from '@/data/agentes';
import { PROVEDORES_DE_IA } from '@/data/integracoes';
import { dados } from '@/data/porta';
import type { Agente, DadosDoAgente, TipoDeAgente, TomDoAgente } from '@/data/tipos';
import { SimuladorDoAgente } from './SimuladorDoAgente';

const NOVO: DadosDoAgente = {
    tipo: 'atendimento',
    nome: '',
    ativo: true,
    tom: 'acolhedor',
    missao: '',
    instrucoes: '',
    temperatura: 0.3,
    ferramentas: ['agenda_horarios_livres', 'catalogo_procedimentos'],
    saudacao: '',
    limites: {},
};

const TIPOS: { value: TipoDeAgente; label: string; ajuda: string }[] = [
    {
        value: 'pre-atendimento',
        label: 'Pré-atendimento',
        ajuda: 'Primeira conversa: entende o que a pessoa quer, informa preço e horário, e passa para a equipe.',
    },
    {
        value: 'atendimento',
        label: 'Atendimento',
        ajuda: 'Conversa completa: consulta a agenda, propõe horário e abre follow-up.',
    },
];

/**
 * O construtor de agentes que falam com o paciente.
 *
 * A escolha central da tela é a lista de ferramentas: **ela é o que o agente
 * pode fazer**, e por isso as que escrevem aparecem marcadas. Nenhuma delas
 * executa sozinha — voltam como proposta para alguém confirmar —, e a tela diz
 * isso ao lado da caixa, não num rodapé que ninguém lê.
 */
export function AgentesDeAtendimento({ agentes, disponivel }: { agentes: Agente[]; disponivel: boolean }) {
    const revalidator = useRevalidator();
    const aviso = useAviso();

    const [emEdicao, setEmEdicao] = useState<Agente | 'novo' | null>(null);
    const [form, setForm] = useState<DadosDoAgente>(NOVO);
    const formSujo = useAlteracaoPendente(form, emEdicao);
    const [confirmar, confirmacao] = useConfirmacao();
    const [salvando, setSalvando] = useState(false);
    const [simulando, setSimulando] = useState<Agente | null>(null);
    /* `null` é "não sei": sem a leitura, o aviso não afirma nada sobre o canal. */
    const [whatsappConectado, setWhatsappConectado] = useState<boolean | null>(null);

    useEffect(() => {
        let vivo = true;
        dados().canaisDaClinica()
            .then((canais) => {
                if (vivo) setWhatsappConectado(canais.some((c) => c.tipo === 'whatsapp' && c.status === 'conectado'));
            })
            .catch(() => { if (vivo) setWhatsappConectado(null); });
        return () => { vivo = false; };
    }, []);

    const abrir = (agente: Agente | 'novo') => {
        setEmEdicao(agente);
        setForm(agente === 'novo'
            ? NOVO
            : {
                tipo: agente.tipo,
                nome: agente.nome,
                ativo: agente.ativo,
                tom: agente.tom,
                missao: agente.missao,
                instrucoes: agente.instrucoes,
                temperatura: agente.temperatura,
                ...(agente.modelo ? { modelo: agente.modelo } : {}),
                ferramentas: [...agente.ferramentas],
                ...(agente.saudacao ? { saudacao: agente.saudacao } : {}),
                limites: agente.limites,
            });
    };

    const salvar = async () => {
        if (!emEdicao) return;
        setSalvando(true);
        try {
            await dados().salvarAgente(emEdicao === 'novo' ? null : emEdicao.id, form);
            aviso({ title: emEdicao === 'novo' ? 'Agente criado' : 'Agente salvo', description: form.nome });
            setEmEdicao(null);
            revalidator.revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível salvar', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        } finally {
            setSalvando(false);
        }
    };

    const remover = async (agente: Agente) => {
        const ok = await confirmar({
            titulo: `Remover o agente ${agente.nome}?`,
            descricao: 'Tom, missão, instruções e ferramentas dele somem, e não voltam.',
            confirmar: 'Remover agente',
        });
        if (!ok) return;
        try {
            await dados().removerAgente(agente.id);
            aviso({ title: 'Agente removido', description: agente.nome });
            revalidator.revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível remover', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        }
    };

    const alternarFerramenta = (id: string, ligada: boolean) => {
        setForm({
            ...form,
            ferramentas: ligada ? [...form.ferramentas, id] : form.ferramentas.filter((f) => f !== id),
        });
    };

    const doPaciente = agentes.filter((a) => a.tipo !== 'copiloto');
    const modelos = PROVEDORES_DE_IA.flatMap((p) => p.modelos.map((m) => ({ value: m, label: `${m} · ${p.nome}` })));
    const limites = (form.limites ?? {}) as { horario?: string; encaminhar?: string };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* A fronteira do que estes agentes fazem hoje, antes que alguém
                conte com um atendimento que ainda não sai. */}
            <Alert tone="warning" dismissible={false} title="Eles ainda não atendem sozinhos">
                {whatsappConectado === true && 'O WhatsApp está conectado, mas a mensagem que chega ainda não é entregue ao agente. '}
                {whatsappConectado === false && 'Nenhum canal de WhatsApp está conectado, e a mensagem que chega ainda não é entregue ao agente. '}
                Por enquanto o agente responde <strong>no simulador</strong>, com os dados de verdade da clínica, e é
                assim que se afina o que ele diz. O que ele propõe — agendar, criar follow-up, criar régua — só
                acontece quando alguém da equipe confirma.
            </Alert>

            <Card
                title="Agentes que falam com o paciente"
                subtitle="Pré-atendimento entende e encaminha; atendimento consulta a agenda e propõe"
                padding={0}
                actions={(
                    <Button size="sm" iconLeft={<Icon name="plus" size={14} />} onClick={() => abrir('novo')}>
                        Novo agente
                    </Button>
                )}
            >
                {doPaciente.length === 0 ? (
                    <EmptyState
                        icon="bot"
                        title="Nenhum agente ainda"
                        description="Crie um de pré-atendimento para começar: ele informa preço e horário, e passa o resto para a equipe."
                    />
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {doPaciente.map((a) => (
                            <div
                                key={a.id}
                                style={{
                                    display: 'flex', alignItems: 'flex-start', gap: 14, padding: 16,
                                    borderTop: '1px solid var(--border)', flexWrap: 'wrap',
                                }}
                            >
                                <span
                                    aria-hidden
                                    style={{
                                        width: 34, height: 34, flex: '0 0 auto', borderRadius: 'var(--radius-sm)',
                                        background: a.ativo ? 'var(--surface-brand-soft)' : 'var(--surface-secondary)',
                                        color: a.ativo ? 'var(--text-brand)' : 'var(--neutral-500)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}
                                >
                                    <Icon name="bot" size={16} />
                                </span>

                                <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
                                        <strong style={{ fontSize: 14, fontWeight: 600 }}>{a.nome}</strong>
                                        <Badge tone={a.ativo ? 'success' : 'neutral'} dot>{a.ativo ? 'Ativo' : 'Desligado'}</Badge>
                                        <Badge tone="neutral">{TIPOS.find((t) => t.value === a.tipo)?.label ?? a.tipo}</Badge>
                                        <Badge tone="info">{tomPorId(a.tom)?.rotulo}</Badge>
                                    </div>
                                    <p style={{ margin: '7px 0 0', fontSize: 12.5, lineHeight: '18px', color: 'var(--text-secondary)' }}>
                                        {a.missao || 'Sem missão declarada.'}
                                    </p>
                                    <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--text-tertiary)' }}>
                                        {a.ferramentas.length === 0
                                            ? 'Nenhuma ferramenta — ele só conversa.'
                                            : `${a.ferramentas.length} ferramentas: ${a.ferramentas.map((f) => ferramentaPorId(f)?.nome ?? f).join(', ')}`}
                                    </p>
                                </div>

                                <div style={{ display: 'flex', gap: 6 }}>
                                    <Button size="sm" variant="secondary" onClick={() => setSimulando(a)}>Simular</Button>
                                    <Button size="sm" variant="ghost" onClick={() => abrir(a)}>Editar</Button>
                                    <Button size="sm" variant="ghost" onClick={() => remover(a)}>Remover</Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            {confirmacao}

            <Modal
                open={emEdicao !== null}
                title={emEdicao === 'novo' ? 'Novo agente' : 'Editar agente'}
                onClose={() => setEmEdicao(null)}
                dirty={formSujo}
                width={720}
                footer={(
                    <>
                        <Button variant="ghost" onClick={() => setEmEdicao(null)}>Cancelar</Button>
                        <Button onClick={salvar} disabled={!form.nome.trim() || salvando}>
                            {salvando ? 'Salvando…' : 'Salvar'}
                        </Button>
                    </>
                )}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(210px,100%),1fr))', gap: 14 }}>
                        <FormField label="Nome" htmlFor="ag-nome" required>
                            <Input id="ag-nome" value={form.nome} onChange={(v) => setForm({ ...form, nome: v })} placeholder="Recepção virtual" />
                        </FormField>

                        <FormField
                            label="Tipo" htmlFor="ag-tipo"
                            hint={TIPOS.find((t) => t.value === form.tipo)?.ajuda}
                        >
                            <Select
                                id="ag-tipo" value={form.tipo}
                                onChange={(v) => setForm({ ...form, tipo: v as TipoDeAgente })}
                                options={TIPOS.map((t) => ({ value: t.value, label: t.label }))}
                            />
                        </FormField>

                        <FormField label="Tom" htmlFor="ag-tom" hint={tomPorId(form.tom)?.descricao}>
                            <Select
                                id="ag-tom" value={form.tom}
                                onChange={(v) => setForm({ ...form, tom: v as TomDoAgente })}
                                options={TONS.map((t) => ({ value: t.id, label: t.rotulo }))}
                            />
                        </FormField>

                        <FormField label={`Temperatura — ${form.temperatura.toFixed(1)}`} hint={descreverTemperatura(form.temperatura)}>
                            <Slider
                                ariaLabel="Temperatura" min={0} max={1} step={0.1} value={form.temperatura}
                                onChange={(v) => setForm({ ...form, temperatura: v })}
                            />
                        </FormField>

                        <FormField label="Modelo" htmlFor="ag-modelo" hint="Vazio usa o padrão da plataforma.">
                            <Select
                                id="ag-modelo" value={form.modelo ?? ''}
                                onChange={(v) => setForm({ ...form, modelo: v || undefined })}
                                options={modelos} placeholder="Padrão da plataforma"
                            />
                        </FormField>

                        <FormField label="Ativo" hint="Desligado, ele não responde nem no simulador.">
                            <Switch checked={form.ativo} onChange={(v) => setForm({ ...form, ativo: v })} label="Ativo" />
                        </FormField>

                        <FormField label="Missão" htmlFor="ag-missao" style={{ gridColumn: '1 / -1' }} hint="Para que ele existe, nas suas palavras.">
                            <Textarea id="ag-missao" rows={2} value={form.missao} onChange={(v) => setForm({ ...form, missao: v })} />
                        </FormField>

                        <FormField
                            label="Instruções" htmlFor="ag-instrucoes" style={{ gridColumn: '1 / -1' }}
                            hint="Convênios aceitos, formas de pagamento, o que nunca prometer."
                        >
                            <Textarea id="ag-instrucoes" rows={3} value={form.instrucoes} onChange={(v) => setForm({ ...form, instrucoes: v })} />
                        </FormField>

                        <FormField label="Saudação" htmlFor="ag-saudacao" style={{ gridColumn: '1 / -1' }} hint="A primeira mensagem, quando é ele quem começa.">
                            <Input
                                id="ag-saudacao" value={form.saudacao ?? ''}
                                onChange={(v) => setForm({ ...form, saudacao: v || undefined })}
                                placeholder="Oi! Aqui é a recepção da Vecchia. Como posso ajudar?"
                            />
                        </FormField>

                        <FormField label="Horário de atendimento" htmlFor="ag-horario" hint="Fora dele, ele avisa e encaminha.">
                            <Input
                                id="ag-horario" value={limites.horario ?? ''}
                                onChange={(v) => setForm({ ...form, limites: { ...limites, horario: v } })}
                                placeholder="Seg a sex, 8h às 19h"
                            />
                        </FormField>

                        <FormField label="Passar para uma pessoa quando" htmlFor="ag-encaminhar">
                            <Input
                                id="ag-encaminhar" value={limites.encaminhar ?? ''}
                                onChange={(v) => setForm({ ...form, limites: { ...limites, encaminhar: v } })}
                                placeholder="A pessoa reclamar, falar de dor ou pedir cancelamento"
                            />
                        </FormField>
                    </div>

                    <div>
                        <span style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>O que ele pode fazer</span>
                        <p style={{ margin: '0 0 10px', fontSize: 12.5, color: 'var(--text-secondary)' }}>
                            As de <strong>consulta</strong> ele executa na hora, com as permissões de quem estiver
                            conversando. As de <strong>escrita</strong> ele apenas propõe — quem confirma é a equipe.
                        </p>

                        {CATEGORIAS_DE_FERRAMENTA.map((categoria) => (
                            <div key={categoria.id} style={{ marginBottom: 12 }}>
                                <span style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: 'var(--tracking-wide)', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
                                    {categoria.rotulo}
                                </span>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
                                    {FERRAMENTAS.filter((f) => f.categoria === categoria.id).map((f) => (
                                        <span key={f.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                            <Checkbox
                                                checked={form.ferramentas.includes(f.id)}
                                                onChange={(v) => alternarFerramenta(f.id, v)}
                                                label={f.nome}
                                            />
                                            {f.escreve && <Badge tone="warning">propõe, não executa</Badge>}
                                            <span style={{ flex: 1, fontSize: 12, color: 'var(--text-tertiary)' }}>{f.descricao}</span>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </Modal>

            {simulando && (
                <SimuladorDoAgente
                    agente={simulando}
                    disponivel={disponivel}
                    onFechar={() => setSimulando(null)}
                />
            )}
        </div>
    );
}
